import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Facebook, Dumbbell, Clock, Star } from "lucide-react";
import { SITE, CLASSES } from "../data/mock";
import { LogoMark } from "./Logo";
import { NewsletterForm } from "./NewsletterForm";

export default function Footer() {
  return (
    <footer className="bg-[#080809] border-t border-white/5">
      {/* Big CTA strip */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423] mb-4">// Ready when you are</p>
            <h2 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
              Stop scrolling.<br />
              <span className="text-neutral-600">Start training.</span>
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/get-started"
              className="inline-flex items-center justify-center bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-8 py-4 transition-all duration-200 hover:translate-y-[-2px]"
            >
              Book a Free Goal Review
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center border border-white/20 hover:border-white/50 text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-8 py-4 transition-colors"
            >
              View Memberships
            </Link>
          </div>
        </div>
      </div>

      {/* The Grit Report — newsletter */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6 lg:items-center min-h-[150px]" data-testid="footer-newsletter">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423] mb-2">// The Grit Report</p>
            <p className="text-sm text-neutral-400 leading-relaxed max-w-md">
              One email a month: training tips, member stories, challenge dates. No spam, unsubscribe anytime.
            </p>
          </div>
          <div className="w-full max-w-md lg:justify-self-end">
            <NewsletterForm source="footer" idPrefix="footer-newsletter" />
            <p className="mt-2 text-[11px] text-neutral-500">
              By subscribing you agree to our <Link to="/privacy-policy" className="underline text-neutral-400 hover:text-white transition-colors">privacy policy</Link>. We never sell your info.
            </p>
          </div>
        </div>
      </div>

      {/* Link columns */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="mb-5">
            <LogoMark className="h-12 w-auto" />
            <div className="mt-3 font-heading font-bold text-white text-lg uppercase tracking-[0.14em]">
              Boston Iron Grit<span className="text-[#9D0423]">.</span>
            </div>
            <div className="font-heading font-semibold text-[9px] tracking-[0.28em] text-neutral-500 uppercase mt-1 whitespace-nowrap">
              We Build Strong Humans
            </div>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed mb-5">
            Coached CrossFit and strength training in West Roxbury since 2011. Beginner-friendly, community-driven, no judgements. Live B.I.G. 365.
          </p>
          <div className="flex items-center gap-3">
            <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2.5 border border-white/10 text-neutral-400 hover:text-white hover:border-[#9D0423] transition-colors">
              <Instagram size={16} />
            </a>
            <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-2.5 border border-white/10 text-neutral-400 hover:text-white hover:border-[#9D0423] transition-colors">
              <Facebook size={16} />
            </a>
            <a href={SITE.social.crossfit} target="_blank" rel="noopener noreferrer" aria-label="CrossFit Affiliate" className="p-2.5 border border-white/10 text-neutral-400 hover:text-white hover:border-[#9D0423] transition-colors">
              <Dumbbell size={16} />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-neutral-500 mb-5">Programs</h3>
          <ul className="space-y-2.5">
            {CLASSES.map((c) => (
              <li key={c.slug}>
                <Link to={`/classes/${c.slug}`} className="text-sm text-neutral-400 hover:text-white transition-colors">{c.name}</Link>
              </li>
            ))}
            <li><Link to="/hyrox" className="text-sm text-[#9D0423] hover:text-[#ff4a56] transition-colors font-medium">HYROX Training</Link></li>
            <li><Link to="/live-big-365" className="text-sm text-[#9D0423] hover:text-[#ff4a56] transition-colors font-medium">Live B.I.G. 365</Link></li>
            <li><Link to="/peptides-glp1-boston" data-testid="footer-peptides-link" className="text-sm text-neutral-400 hover:text-white transition-colors">Peptides & GLP-1s</Link></li>
            <li><Link to="/gold-standard" className="text-sm text-[#E8C766] hover:text-white transition-colors font-medium">Gold Standard Programs</Link></li>
            <li><Link to="/classes" className="text-sm text-neutral-400 hover:text-white transition-colors">All Programs →</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-neutral-500 mb-5">The Gym</h3>
          <ul className="space-y-2.5">
            {[
              { label: "About Us", to: "/about" },
              { label: "Class Schedule", to: "/schedule" },
              { label: "Memberships & Pricing", to: "/pricing" },
              { label: "Visiting Athletes / Drop-In", to: "/drop-in" },
              { label: "West Roxbury & Nearby Towns", to: "/west-roxbury-gym" },
              { label: "Blog", to: "/blog" },
              { label: "Book Online", to: "/book" },
              { label: "Contact", to: "/contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-neutral-400 hover:text-white transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-neutral-500 mb-5">Find Us</h3>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <MapPin size={15} className="text-[#9D0423] mt-0.5 shrink-0" />
              <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-400 hover:text-white transition-colors leading-relaxed">
                {SITE.address.street}<br />{SITE.address.city}, {SITE.address.state} {SITE.address.zip}<br />
                <span className="text-neutral-600 text-xs">({SITE.address.unit})</span>
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={15} className="text-[#9D0423] shrink-0" />
              <a href={SITE.phoneHref} className="text-sm text-neutral-400 hover:text-white transition-colors">{SITE.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={15} className="text-[#9D0423] shrink-0" />
              <a href={`mailto:${SITE.email}`} className="text-sm text-neutral-400 hover:text-white transition-colors">{SITE.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Star size={15} className="text-[#9D0423] shrink-0" />
              <a href={SITE.reviewUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-google-review-link" className="text-sm text-neutral-400 hover:text-white transition-colors">Leave us a Google review</a>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={15} className="text-[#9D0423] mt-0.5 shrink-0" />
              <div className="text-sm text-neutral-400 leading-relaxed">
                {SITE.hours.map((h) => (
                  <div key={h.days}><span className="text-neutral-500">{h.days}:</span> {h.time}</div>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-neutral-600">
            © {new Date().getFullYear()} {SITE.name} · West Roxbury, MA. All rights reserved.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-neutral-600" aria-label="Legal and member services">
            <Link to="/privacy-policy" className="hover:text-neutral-300 transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-neutral-300 transition-colors" data-testid="footer-terms">Terms of Use</Link>
            <Link to="/accessibility-statement" className="hover:text-neutral-300 transition-colors" data-testid="footer-accessibility">Accessibility</Link>
            <Link to="/membership-hold-request" className="hover:text-neutral-300 transition-colors" data-testid="footer-hold">Membership Hold</Link>
            <Link to="/membership-cancellation-request" className="hover:text-neutral-300 transition-colors" data-testid="footer-cancel">Cancellation</Link>
          </nav>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-600">Live B.I.G. 365</p>
          <p className="text-[11px] text-neutral-600">
            <a
              href="https://digitalmedianinja.com/services/web-design"
              target="_blank"
              rel="noopener"
              data-testid="footer-design-credit"
              className="hover:text-neutral-300 transition-colors"
            >
              Custom Web Design by Digital Media Ninja
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
